import {useMemo} from 'react';
import {Control, Controller, ControllerProps, useWatch, ValidationRule} from 'react-hook-form';
import AppAutoComplete, {AppAutoCompleteProps} from './AppAutoComplete';
import {SelectOptionsType} from 'global';

export type AppControllerAutoCompleteMultiProps = Omit<
    AppAutoCompleteProps<SelectOptionsType, true>,
    'name' | 'required'
> &
    Omit<ControllerProps, 'render' | 'control'> & {
        required?: string | ValidationRule<boolean>;
        control: Control<any, any, any>;
        shouldReturnValueOnly?: boolean;
    };

function getStringValue(val: SelectOptionsType[]): string[] {
    return val.map(x => x.value);
}

export function AppControllerAutoCompleteMulti({
    name,
    control,
    rules,
    required,
    shouldReturnValueOnly = false,
    ...rest
}: AppControllerAutoCompleteMultiProps) {
    const watchedValue = useWatch({control, name});

    const selectedValue = useMemo(() => {
        if (shouldReturnValueOnly) {
            return rest.options?.filter(opt => (watchedValue as string[])?.includes(opt.value)) || [];
        } else {
            return watchedValue || [];
        }
    }, [watchedValue, rest.options, shouldReturnValueOnly]);

    return (
        <Controller
            name={name}
            rules={{required, ...rules}}
            control={control}
            render={({field, fieldState: {invalid, error}}) => (
                <AppAutoComplete
                    {...field}
                    multiple={true}
                    style={rest.style}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    disabled={rest.disabled || false}
                    onInputChange={rest.onInputChange}
                    disableClearable={rest.disableClearable || false}
                    options={rest.options || []}
                    loading={rest.loading || false}
                    label={rest.label}
                    descriptionText={rest.descriptionText}
                    fullWidth={rest.fullWidth || false}
                    size={rest.size || 'medium'}
                    onScrollBottomHandler={rest.onScrollBottomHandler}
                    required={!!required}
                    value={selectedValue}
                    onChange={(_e, _value) => field.onChange(shouldReturnValueOnly ? getStringValue(_value) : _value)}
                    isInvalid={invalid}
                    validationMessage={error?.type === 'required' ? `${rest.label} is required` : error?.message || ''}
                />
            )}
        />
    );
}

export default AppControllerAutoCompleteMulti;
